import React, { useState, useEffect } from "react";
import SwModal from "../SwModal/SwModal";
import SpInput from "../SpInput/SpInput";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";
import { Upload } from "lucide-react";
import * as Yup from "yup";
import { addEventVendor, updateEventVendorInfo } from "../../redux/actions/eventVendor";
import {
  selectAddEventVendorLoading,
  selectUpdateEventVendorLoading,
  selectAddEventVendorSuccess,
  selectAddEventVendorError,
  selectUpdateInfoSuccess,
  selectUpdateEventVendorError
} from "../../redux/selectors/eventVendorSelectors"; // Adjust path as needed

// Import action creators to reset states if available
// You might need to create these reset actions in your redux actions
// import { resetAddEventVendorState, resetUpdateEventVendorState } from "../../redux/actions/eventVendor";
import { listEvents } from './../../redux/actions/event';

const SwVendorModal = ({ onClose, open, mode = "add", vendorData = null }) => {
  const dispatch = useDispatch();
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [hasShownError, setHasShownError] = useState({ add: false, update: false });
  
  // Redux state selectors - Use specific loading selectors
  const addLoading = useSelector(selectAddEventVendorLoading);
  const updateLoading = useSelector(selectUpdateEventVendorLoading);
  const addSuccess = useSelector(selectAddEventVendorSuccess);
  const addError = useSelector(selectAddEventVendorError);
  const updateSuccess = useSelector(selectUpdateInfoSuccess);
  const updateError = useSelector(selectUpdateEventVendorError);

  const isEditMode = mode === "edit";
  
  // Get the appropriate loading state based on mode
  const isLoading = isEditMode ? updateLoading : addLoading;

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .max(100, "Name must be less than 100 characters")
      .required("Name is required"),
    business_name: Yup.string()
      .min(2, "Business name must be at least 2 characters")
      .max(100, "Business name must be less than 100 characters")
      .required("Business name is required"),
    phone_number: Yup.string()
      .matches(/^[\d+\-\s()]+$/, "Please enter a valid phone number")
      .min(10, "Phone number must be at least 10 digits")
      .max(15, "Phone number must be less than 15 digits")
      .required("Phone number is required"),
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("Email is required"),
    brand_color: Yup.string()
      .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, "Please enter a valid hex color code (e.g., #F4AC29)")
      .required("Brand color is required"),
    beneficiary_account_id: Yup.string()
      .min(3, "Account ID must be at least 3 characters")
      .required("Beneficiary account ID is required"),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      business_name: '',
      phone_number: '',
      email: '',
      brand_color: '#F4AC29',
      beneficiary_account_id: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        // Reset error tracking before each submission attempt
        setHasShownError({ add: false, update: false });
        
        // Prepare payload matching API structure
        const payload = {
          name: values.name.trim(),
          business_name: values.business_name.trim(),
          phone_number: values.phone_number.trim(),
          email: values.email.trim().toLowerCase(),
          brand_color: values.brand_color,
          account_id: values.beneficiary_account_id.trim(),
        };

        // If there's a logo file, we'll need to handle it separately
        // depending on your API requirements
        if (logoFile) {
          // Option 1: If API accepts FormData
          const formData = new FormData();
          Object.keys(payload).forEach(key => {
            formData.append(key, payload[key]);
          });
          formData.append('logo', logoFile);
          payload.formData = formData;
        }

        if (isEditMode && vendorData?.id) {
          // Update existing vendor
          payload.id = vendorData.id;
          dispatch(updateEventVendorInfo(payload));
        } else {
          // Create new vendor
          dispatch(addEventVendor(payload));
        }
      } catch (error) {
        console.error("Form submission error:", error);
        message.error("An unexpected error occurred. Please try again.");
      }
    }
  });

  // Handle success states
  useEffect(() => {
    if (addSuccess && !isEditMode) {
      message.success("Vendor created successfully!");
      
     dispatch(listEvents());

     setTimeout(() => {
        handleClose();
      }, 1000); 

    }
  }, [addSuccess, isEditMode]);

  useEffect(() => {
    if (updateSuccess && isEditMode) {
      message.success("Vendor updated successfully!");
      handleClose();
      dispatch(listEvents());

     setTimeout(() => {
        handleClose();
      }, 1000); 

    }
  }, [updateSuccess, isEditMode]);

  // Handle error states - Now shows errors on every attempt since hasShownError is reset on submit
  useEffect(() => {
    if (addError && !isEditMode && !hasShownError.add) {
      const errorMessage = typeof addError === 'string' ? addError : addError?.message || "Failed to create vendor";
      message.error(errorMessage);
      setHasShownError(prev => ({ ...prev, add: true }));
    }
  }, [addError, isEditMode, hasShownError.add]);

  useEffect(() => {
    if (updateError && isEditMode && !hasShownError.update) {
      const errorMessage = typeof updateError === 'string' ? updateError : updateError?.message || "Failed to update vendor";
      message.error(errorMessage);
      setHasShownError(prev => ({ ...prev, update: true }));
    }
  }, [updateError, isEditMode, hasShownError.update]);

  // Reset states when modal opens or mode changes
  useEffect(() => {
    if (open) {
      // Reset error tracking when modal opens
      setHasShownError({ add: false, update: false });
      
      // Clear any existing messages
      message.destroy();
    }
  }, [open, mode]);

  // Populate form when in edit mode
  useEffect(() => {
    if (isEditMode && vendorData) {
      formik.setValues({
        name: vendorData.name || '',
        business_name: vendorData.business_name || vendorData.businessName || '',
        phone_number: vendorData.phone_number || vendorData.phoneNumber || '',
        email: vendorData.email || '',
        brand_color: vendorData.brand_color || vendorData.brandColor || vendorData.brandColorCode || '#F4AC29',
        beneficiary_account_id: vendorData.account_id || vendorData.accountId || '',
      });
      
      // Set logo preview if vendor has existing logo
      if (vendorData.logoUrl || vendorData.logo_url) {
        setLogoPreview(vendorData.logoUrl || vendorData.logo_url);
      }
    } else {
      // Reset form for add mode
      formik.resetForm();
      setLogoPreview(null);
    }
  }, [isEditMode, vendorData, open]);

  const handleClose = () => {
    formik.resetForm();
    setLogoFile(null);
    setLogoPreview(null);
    setHasShownError({ add: false, update: false }); // Reset error tracking
    message.destroy(); // Clear any existing messages
    onClose();
  };

  const handleSaveChanges = () => {
    formik.submitForm();
  };

  const handleChange = (key, value) => {
    formik.setFieldValue(key, value);
    // Touch the field to trigger validation display
    formik.setFieldTouched(key, true);
  };

  const handleBlur = (fieldName) => {
    formik.setFieldTouched(fieldName, true);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        message.error("Please upload a valid image file (JPEG, PNG, GIF)");
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        message.error("File size must be less than 5MB");
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Helper function to get field error
  const getFieldError = (fieldName) => {
    return formik.touched[fieldName] && formik.errors[fieldName] ? formik.errors[fieldName] : null;
  };

  // Dynamic modal content based on mode
  const modalTitle = isEditMode ? "Edit Vendor" : "Create Vendor";
  const modalSubtitle = isEditMode 
    ? "Update vendor's information below" 
    : "Enter vendor's information below";
  const saveButtonText = isEditMode ? "Update Vendor" : "Create Vendor";

  return (
    <SwModal
      title={modalTitle}
      subtitle={modalSubtitle}
      onClose={handleClose}
      open={open}
      onSaveChanges={handleSaveChanges}
      saveButtonLoading={isLoading}
      saveButtonText={saveButtonText}
      width="524px"
    >
      <form onSubmit={formik.handleSubmit}>
        <div style={{ marginBottom: 24 }}>
          <SpInput
            htmlFor="name"
            label="Name"
            placeholder="Enter vendor's name"
            value={formik.values.name}
            onChange={(e) => handleChange('name', e.target.value)}
            onBlur={() => handleBlur('name')}
            error={getFieldError('name')}
            required
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <SpInput
            htmlFor="business_name"
            label="Business Name"
            placeholder="Enter business name"
            value={formik.values.business_name}
            onChange={(e) => handleChange('business_name', e.target.value)}
            onBlur={() => handleBlur('business_name')}
            error={getFieldError('business_name')}
            required
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <SpInput
            htmlFor="phone_number"
            label="Phone Number"
            placeholder="Enter phone number (e.g., 08061372140)"
            value={formik.values.phone_number}
            onChange={(e) => handleChange('phone_number', e.target.value)}
            onBlur={() => handleBlur('phone_number')}
            error={getFieldError('phone_number')}
            required
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <SpInput
            htmlFor="email"
            label="Email"
            type="email"
            placeholder="Enter email address"
            value={formik.values.email}
            onChange={(e) => handleChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            error={getFieldError('email')}
            required
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <SpInput
            htmlFor="brand_color"
            label="Brand Color"
            placeholder="#F4AC29"
            value={formik.values.brand_color}
            onChange={(e) => handleChange('brand_color', e.target.value)}
            onBlur={() => handleBlur('brand_color')}
            error={getFieldError('brand_color')}
            required
          />
          {formik.values.brand_color && (
            <div style={{ 
              marginTop: 8, 
              width: 40, 
              height: 20, 
              backgroundColor: formik.values.brand_color,
              border: '1px solid #ddd',
              borderRadius: 4
            }} />
          )}
        </div>

        <div style={{ marginBottom: 24 }}>
          <SpInput
            htmlFor="beneficiary_account_id"
            label="Beneficiary Account ID"
            placeholder="Enter beneficiary account ID"
            value={formik.values.beneficiary_account_id}
            onChange={(e) => handleChange('beneficiary_account_id', e.target.value)}
            onBlur={() => handleBlur('beneficiary_account_id')}
            error={getFieldError('beneficiary_account_id')}
            required
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label className="sw-file-upload-label">
            {isEditMode ? "Update Logo" : "Upload Logo"} (Optional)
          </label>
          <div className="sw-file-upload-wrapper">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="sw-file-upload-input"
              id="logo-upload"
            />
            <label htmlFor="logo-upload" className="sw-file-upload-button">
              <span className="sw-file-upload-text">
                {logoFile ? logoFile.name : (isEditMode ? "Change Logo" : "Choose Logo")}
              </span>
              <Upload size={20} className="sw-file-upload-icon" />
            </label>
            
            {logoPreview && (
              <div className="sw-file-upload-preview">
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="sw-file-upload-preview-image"
                />
              </div>
            )}
          </div>
          <small style={{ color: '#666', fontSize: 12 }}>
            Supported formats: JPEG, PNG, GIF. Max size: 5MB
          </small>
        </div>
      </form>
    </SwModal>
  );
};

export default SwVendorModal;